/**
 * Auth Store (Pinia)
 *
 * Manages Ice Master authentication state.
 * Bridges Firebase Auth (client SDK) with the MongoDB user via backend API.
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authService } from '@/services/auth.service';
import { api } from '@/services/api';
import type { Unsubscribe } from 'firebase/auth';

// =============================================================================
// Ice Master User (MongoDB representation)
// =============================================================================

export interface IceMasterUser {
  _id: string;
  firebaseUid: string;
  email: string;
  displayName?: string;
  username?: string;
  avatar?: string;
  homeVenueId?: string;
  home_club?: string;
  fan_type?: 'in_arena' | 'remote';
  supportedLeague?: string;
  points: number;
  level: number;
  levelName: string;
  role: string;
  onboardingComplete: boolean;
  dob_verified: boolean;
  suspendedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Store
// =============================================================================

export const useAuthStore = defineStore('auth', () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const user = ref<IceMasterUser | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const token = ref<string | null>(null);

  let unsubscribe: Unsubscribe | null = null;

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  const isAuthenticated = computed(() => !!user.value);
  const displayName = computed(() => user.value?.displayName || user.value?.email || 'Fan');
  const needsOnboarding = computed(() => user.value && !user.value.onboardingComplete);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Register with email/password — creates Firebase account + MongoDB user.
   */
  async function register(email: string, password: string, displayNameValue?: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const firebaseUser = await authService.signUpWithEmail(email, password);
      const idToken = await firebaseUser.getIdToken();

      const response = await api.post<{ user: IceMasterUser }>('/auth/register', {
        token: idToken,
        displayName: displayNameValue || firebaseUser.displayName,
      });

      if (response.success && response.data) {
        user.value = response.data.user;
        token.value = idToken;
        api.setAuthToken(idToken);
        return true;
      }

      error.value = response.error || 'Registration failed';
      return false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Registration failed';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with email/password — authenticates via Firebase, fetches MongoDB user.
   */
  async function login(email: string, password: string): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const firebaseUser = await authService.signInWithEmail(email, password);
      const idToken = await firebaseUser.getIdToken();

      const response = await api.post<{ user: IceMasterUser }>('/auth/login', {
        token: idToken,
      });

      if (response.success && response.data) {
        user.value = response.data.user;
        token.value = idToken;
        api.setAuthToken(idToken);
        return true;
      }

      error.value = response.error || 'Login failed';
      return false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with Google OAuth — popup flow, then register-or-login on backend.
   */
  async function loginWithGoogle(): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const firebaseUser = await authService.signInWithGoogle();
      const idToken = await firebaseUser.getIdToken();

      // Use register endpoint — it returns existing user if already registered
      const response = await api.post<{ user: IceMasterUser }>('/auth/register', {
        token: idToken,
        displayName: firebaseUser.displayName,
      });

      if (response.success && response.data) {
        user.value = response.data.user;
        token.value = idToken;
        api.setAuthToken(idToken);
        return true;
      }

      error.value = response.error || 'Google sign-in failed';
      return false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Google sign-in failed';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Logout — sign out of Firebase and clear all state.
   */
  async function logout(): Promise<void> {
    isLoading.value = true;

    try {
      await authService.signOut();
    } catch {
      // Continue even if Firebase sign-out fails
    } finally {
      user.value = null;
      token.value = null;
      api.clearAuthToken();
      api.clearSession();
      isLoading.value = false;
    }
  }

  /**
   * Fetch the current user profile from the backend.
   */
  async function fetchUser(): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await api.get<{ user: IceMasterUser }>('/auth/me');

      if (response.success && response.data) {
        user.value = response.data.user;
      }
    } catch {
      user.value = null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Update the current user's profile on the backend.
   */
  async function updateProfile(updates: Partial<IceMasterUser>): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await api.put<{ user: IceMasterUser }>('/auth/me', updates);

      if (response.success && response.data) {
        user.value = response.data.user;
        return true;
      }

      error.value = response.error || 'Update failed';
      return false;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Update failed';
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Initialize the store — listen to Firebase auth state and refresh the token.
   */
  function initialize(): void {
    if (unsubscribe) return; // Already listening

    unsubscribe = authService.onAuthStateChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          token.value = idToken;
          api.setAuthToken(idToken);

          // Fetch the MongoDB user if we do not have one yet
          if (!user.value) {
            await fetchUser();
          }
        } catch {
          // Token fetch failed — user state will remain null
        }
      } else {
        user.value = null;
        token.value = null;
        api.clearAuthToken();
      }
    });
  }

  function clearError(): void {
    error.value = null;
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    user,
    isLoading,
    error,
    token,

    // Getters
    isAuthenticated,
    displayName,
    needsOnboarding,

    // Actions
    register,
    login,
    loginWithGoogle,
    logout,
    fetchUser,
    updateProfile,
    initialize,
    clearError,
  };
});
